import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MetricCard, CHART_COLORS } from "@/components/MetricCard";
import { 
  Eye, 
  MousePointer, 
  Target, 
  Users, 
  DollarSign, 
  MapPin,
  Image,
  Megaphone,
  BarChart3,
  Building2
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";
import { format } from "date-fns";

const tierColors = {
  BRONZE: "tier-bronze",
  SILVER: "tier-silver", 
  GOLD: "tier-gold"
};

export default function Dashboard() {
  const { user, token, isAdmin } = useAuth();

  // A first name only when there is one to take. Existing accounts still carry the
  // business name in display_name - registration only started storing the person's name
  // after round 2 - so splitting unconditionally would keep greeting them by half their
  // business. `name` is the person's when it is set at all.
  const personName = user?.name || user?.display_name || "";
  const greetingName = personName.includes(" ") && user?.name
    ? personName.split(" ")[0]
    : personName;
  const [analytics, setAnalytics] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [account, setAccount] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [dealsStats, setDealsStats] = useState(null);
  const [campaignHealth, setCampaignHealth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [analyticsRes, timeseriesRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { headers }),
        axios.get(`${API}/analytics/timeseries?days=30`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setTimeseries(timeseriesRes.data);

      if (user.account_id) {
        const accountRes = await axios.get(`${API}/accounts/me`, { headers });
        setAccount(accountRes.data);
        
        // Fetch campaign health scores
        try {
          const healthRes = await axios.get(`${API}/analytics/campaign-health?days=30`, { headers });
          setCampaignHealth(healthRes.data);
        } catch (err) {
          console.log("Campaign health not available");
        }
      }

      if (isAdmin) {
        const statsRes = await axios.get(`${API}/admin/stats`, { headers });
        setAdminStats(statsRes.data);
      }

      // Fetch deals stats from ledger summary (for advertisers)
      if (user.account_id) {
        try {
          const ledgerRes = await axios.get(`${API}/billing/ledger/summary`, { headers });
          const ledgerData = ledgerRes.data;
          
          // Extract deals-related metrics
          const saveToCart = ledgerData.by_type?.SAVE_TO_CART || { count: 0, amount: 0 };
          const dealRedemption = ledgerData.by_type?.DEAL_REDEMPTION || { count: 0, amount: 0 };
          
          setDealsStats({
            saved: saveToCart.count,
            redeemed: dealRedemption.count,
            conversionRate: saveToCart.count > 0 
              ? Math.round((dealRedemption.count / saveToCart.count) * 100) 
              : 0,
            revenue: saveToCart.amount + dealRedemption.amount
          });
        } catch (err) {
          // Deals stats not critical, continue without
          console.log("Deals stats not available");
        }
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-heavy rounded-lg p-3 border border-border shadow-xl">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'spend' ? `$${entry.value.toFixed(2)}` : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            {/* Greet the PERSON. display_name used to be set to the business name at
                registration, so this rendered "Welcome back, Shark" for "Shark Bar"
                (round 2). Registration now stores the contact's name; this falls back
                to the whole string rather than its first word, because a first word is
                only a first name when the value really is a person. */}
            {isAdmin ? "Platform Overview" : `Welcome back${greetingName ? `, ${greetingName}` : ""}`}
          </p>
        </div>
        {account && (
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={`px-3 py-1 text-sm ${
              account.status === 'PENDING_VERIFICATION' ? 'status-pending' :
              account.status === 'VERIFIED' ? 'status-verified' :
              account.status === 'ACTIVE' ? 'status-active' :
              'status-active'
            }`}>
              {account.status === 'PENDING_VERIFICATION' ? 'Pending Verification' : account.status}
            </Badge>
          </div>
        )}
      </div>

      {/* Account Profile Card - For Advertisers */}
      {account && (
        <Card className="bg-card border-border overflow-hidden" data-testid="account-profile-card">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {/* Logo Section */}
              <div className="md:w-48 p-6 flex items-center justify-center bg-secondary/30 border-b md:border-b-0 md:border-r border-border">
                {account.logo_url ? (
                  <img 
                    src={account.logo_url} 
                    alt={account.business_name} 
                    className="w-28 h-28 object-contain rounded-lg"
                    data-testid="company-logo"
                  />
                ) : (
                  <Link to="/profile" className="group">
                    <div className="w-28 h-28 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                      <Building2 className="h-8 w-8 mb-2" />
                      <span className="text-xs text-center">Add Logo</span>
                    </div>
                  </Link>
                )}
              </div>

              {/* Account Details */}
              <div className="flex-1 p-6">
                {/* Status badge intentionally omitted here - the account status indicator
                    lives in the page header (top-right) to avoid duplication. (#22) */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{account.business_name}</h2>
                    <p className="text-sm text-muted-foreground font-mono">ABN: {account.abn}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Primary Contact */}
                  <div className="p-3 rounded-lg bg-secondary/30 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Primary Contact</p>
                    </div>
                    <p className="font-medium text-sm">{account.primary_contact?.name || 'Not set'}</p>
                    <p className="text-xs text-muted-foreground">{account.primary_contact?.email || 'Not set'}</p>
                    <p className="text-xs text-muted-foreground">{account.primary_contact?.phone || 'Not set'}</p>
                  </div>
                  {/* Emergency / Secondary Contact */}
                  <div className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-orange-500 uppercase tracking-wider font-medium">Emergency Contact</p>
                    </div>
                    {account.emergency_contacts?.[0] ? (
                      <>
                        <p className="font-medium text-sm">{account.emergency_contacts[0].name}</p>
                        <p className="text-xs text-muted-foreground">{account.emergency_contacts[0].email}</p>
                        <p className="text-xs text-muted-foreground">{account.emergency_contacts[0].phone}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-sm text-muted-foreground">Not set</p>
                        <p className="text-xs text-muted-foreground">Not set</p>
                        <p className="text-xs text-muted-foreground">Not set</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Stats Row */}
      {isAdmin && adminStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Accounts</p>
                  <p className="text-2xl font-bold mt-1">{adminStats.total_accounts}</p>
                </div>
                <Users className="h-8 w-8 text-primary/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Campaigns</p>
                  <p className="text-2xl font-bold mt-1">{adminStats.active_campaigns}</p>
                </div>
                <Megaphone className="h-8 w-8 text-green-500/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Revenue</p>
                  <p className="text-2xl font-bold mt-1">${adminStats.monthly_revenue?.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-yellow-500/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Locations</p>
                  <p className="text-2xl font-bold mt-1">{adminStats.total_locations}</p>
                </div>
                <MapPin className="h-8 w-8 text-purple-500/60" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overall Performance Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold">Overall Performance</h2>
        
        {/* Main Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Views */}
          <MetricCard
            type="impressions"
            value={analytics?.impressions}
            testId="metric-views"
          />

          {/* Clicks + Saved Offers nested underneath (#1; CTR removed per P2-1) */}
          <MetricCard
            type="clicks"
            value={analytics?.clicks}
            savedOffers={dealsStats?.saved || 0}
            testId="metric-clicks"
          />

          {/* Followers (#3) — already returned by /analytics/summary as favorite_count */}
          <MetricCard
            type="favorites"
            value={analytics?.favorite_count || 0}
            testId="metric-followers"
          />

          {/* Redeems */}
          <MetricCard
            type="redeems"
            value={dealsStats?.redeemed || 0}
            testId="metric-redeems"
          />

          {/* Delivered Customers */}
          <MetricCard
            type="cpdc"
            value={(analytics?.cpdc_direct || 0) + (analytics?.cpdc_proximity || 0) + (analytics?.cpdc_prompted || 0)}
            direct={analytics?.cpdc_direct || 0}
            proximity={analytics?.cpdc_proximity || 0}
            prompted={analytics?.cpdc_prompted || 0}
            testId="metric-cpdc"
          />
        </div>
      </div>

      {/* Campaign Health Scores */}
      {account && campaignHealth.length > 0 && (
        <Card className="bg-card border-border" data-testid="campaign-health-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Campaign Health
                </CardTitle>
                <CardDescription>
                  Overall campaign health based on performance metrics and comparison with similar campaigns.
                </CardDescription>
              </div>
              <Link to="/analytics">
                <Button variant="ghost" size="sm" className="text-xs">View Details</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {campaignHealth.map((c) => {
                // Colours only. The WORDING now comes from the API - spec 7.7 words it
                // "Excellent" / "Needs Improvement", and these labels previously said
                // "Strong" / "Average" / "Needs Attention", none of which the spec uses.
                // Kept as a fallback for a backend that predates `rating`.
                const gradeConfig = {
                  A: { bg: "bg-green-500/15", border: "border-green-500/30", text: "text-green-500", label: "Excellent" },
                  B: { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-500", label: "Good" },
                  C: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", label: "Needs Improvement" },
                }[c.grade] || { bg: "bg-muted", border: "border-border", text: "text-muted-foreground", label: "Unrated" };
                return (
                  <div key={c.id} className={`flex items-center gap-4 p-3 rounded-lg ${gradeConfig.bg} border ${gradeConfig.border}`} data-testid={`health-${c.id}`}>
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${gradeConfig.text} border ${gradeConfig.border}`}>
                      {c.grade}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{c.name}</p>
                        <Badge variant="outline" className={`text-xs shrink-0 ${
                          c.tier === 'GOLD' ? 'border-yellow-500/40 text-yellow-500' :
                          c.tier === 'SILVER' ? 'border-gray-400/40 text-gray-400' :
                          'border-orange-700/40 text-orange-700'
                        }`}>{c.tier}</Badge>
                      </div>
                      <p className={`text-xs mt-0.5 ${gradeConfig.text}`}>{c.rating || gradeConfig.label}</p>
                    </div>
                    {/* Delivered and Views were here and are gone (round 9, TP-26).
                        They repeat Analytics, which is one click away and is where a
                        merchant goes for numbers; and at zero they made every row read
                        as a failure whatever grade it carried. What is left is the
                        grade, the name and the rating, which is what "campaign health"
                        means. */}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      <Card className="bg-card border-border" data-testid="performance-chart">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold">Performance Overview</CardTitle>
              <CardDescription>Last 30 days activity</CardDescription>
            </div>
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {timeseries.length > 0 && timeseries.some(d => ((d.impressions||0)+(d.clicks||0)+(d.delivered_customers||0)) > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="date" 
                  stroke="#A1A1AA"
                  fontSize={12}
                  tickFormatter={(value) => format(new Date(value), "MMM d")}
                />
                <YAxis yAxisId="left" stroke="#3B82F6" fontSize={12} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <YAxis yAxisId="right" orientation="right" stroke="#A1A1AA" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="impressions"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorImpressions)"
                  name="Views"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="clicks"
                  stroke="#F97316"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorClicks)"
                  name="Clicks"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="delivered_customers"
                  stroke="#A855F7"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDelivered)"
                  name="Delivered Customers"
                />
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <BarChart3 className="h-10 w-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">No performance data yet</p>
                <p className="text-xs mt-1 max-w-xs">Once your campaigns start delivering, your 30-day trend will appear here.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
